<script lang="ts">
  import { router } from '@inertiajs/svelte';
  // Scoped admin palette (cafe-latte). See css/themes/cafe-latte.css.
  import '@/css/themes/cafe-latte.css';

  interface FeedbackPoint {
    category: string;
    text: string;
  }
  interface Mission1File {
    field: string;
    filename: string;
    attachment_id: string;
    block_id: string;
  }
  interface Submission {
    id: string;
    student_name: string;
    team: string;
    team_short: string;
    mentor: string;
    week_number: number;
    cohort: string;
    impression: string | null;
    next_action: string | null;
    mentor_feedback_summary: string | null;
    mentor_feedback_points: FeedbackPoint[];
    mission1_files: Mission1File[];
    mission2_files: string[];
    has_feedback: boolean;
    has_mission1: boolean;
    has_mission2_text: boolean;
    has_mission2_files: boolean;
  }
  interface Stats {
    total: number;
    with_mission1: number;
    with_mission2_text: number;
    with_mission2_files: number;
    with_feedback: number;
    by_mentor: Record<string, number>;
    by_team: Record<string, number>;
  }
  interface Props {
    submissions: Submission[];
    cohort: string;
    week: number;
    stats: Stats;
    filter: { mentor: string | null; team: string | null };
  }
  let { submissions, cohort, week, stats, filter }: Props = $props();

  const EXEMPLARY = ['이민기', '이상훈', '홍주아', '정재혁', '윤장원', '박소연'];
  const ISSUE_NO_TEXT = ['강슬기', '이형광'];
  const ISSUE_FORMAT = ['정민우', '송지현', '김현태', '서유진'];

  let activeFilter = $state('all');

  function setFilter(f: string) {
    activeFilter = f;
  }

  function getFilteredSubmissions() {
    return submissions.filter(s => {
      if (activeFilter === 'all') return true;
      if (activeFilter === '승한멘토') return s.mentor === '승한멘토';
      if (activeFilter === '유진멘토') return s.mentor === '유진멘토';
      if (activeFilter === 'issue') return ISSUE_NO_TEXT.includes(s.student_name) || ISSUE_FORMAT.includes(s.student_name);
      if (activeFilter === 'exemplary') return EXEMPLARY.includes(s.student_name);
      if (activeFilter === 'feedback') return s.has_feedback;
      return true;
    });
  }

  function getCardClass(s: Submission) {
    let cls = 'card';
    if (ISSUE_NO_TEXT.includes(s.student_name)) cls += ' no-text';
    else if (ISSUE_FORMAT.includes(s.student_name)) cls += ' has-issue';
    else if (EXEMPLARY.includes(s.student_name)) cls += ' exemplary';
    if (s.has_feedback) cls += ' has-feedback';
    return cls;
  }

  function fileExt(name: string) {
    const m = name.match(/\.([a-z]+)$/i);
    return m ? m[1].toLowerCase() : '';
  }

  function getCatClass(cat: string) {
    if (cat.includes('✅') || cat.includes('👍')) return 'cat-good';
    if (cat.includes('🎯') || cat.includes('💡') || cat.includes('📋')) return 'cat-action';
    if (cat.includes('⚠️')) return 'cat-warn';
    if (cat.includes('📅') || cat.includes('📊')) return 'cat-long';
    return '';
  }

  // Group by mentor → team
  function groupSubmissions(subs: Submission[]) {
    const groups: Record<string, Record<string, Submission[]>> = {};
    subs.forEach(s => {
      if (!groups[s.mentor]) groups[s.mentor] = {};
      if (!groups[s.mentor][s.team_short]) groups[s.mentor][s.team_short] = [];
      groups[s.mentor][s.team_short].push(s);
    });
    return groups;
  }

  let filtered = $derived(getFilteredSubmissions());
  let grouped = $derived(groupSubmissions(filtered));
</script>

<div class="container cafe-latte">
  <header>
    <h1>KITECH 2026 행정부문 생성형 AI 심화교육</h1>
    <div class="subtitle">{week}주차 미션 결과 · {cohort} · 멋쟁이사자처럼</div>
    <div class="stats">
      <div class="stat"><div class="number">{stats.total}</div><div class="label">전체 학생</div></div>
      <div class="stat"><div class="number">{stats.with_mission1}</div><div class="label">1번 미션 제출</div></div>
      <div class="stat"><div class="number">{stats.with_mission2_text}</div><div class="label">2번 미션 텍스트</div></div>
      <div class="stat"><div class="number">{stats.with_mission2_files}</div><div class="label">2번 미션 첨부</div></div>
      <div class="stat"><div class="number">{stats.with_feedback}</div><div class="label">멘토 피드백</div></div>
    </div>
  </header>

  <div class="filter-bar">
    <button class="filter-btn" class:active={activeFilter === 'all'} onclick={() => setFilter('all')}>전체</button>
    <button class="filter-btn" class:active={activeFilter === '승한멘토'} onclick={() => setFilter('승한멘토')}>승한멘토</button>
    <button class="filter-btn" class:active={activeFilter === '유진멘토'} onclick={() => setFilter('유진멘토')}>유진멘토</button>
    <button class="filter-btn" class:active={activeFilter === 'issue'} onclick={() => setFilter('issue')}>⚠️ 이슈만</button>
    <button class="filter-btn" class:active={activeFilter === 'exemplary'} onclick={() => setFilter('exemplary')}>🌟 모범만</button>
    <button class="filter-btn" class:active={activeFilter === 'feedback'} onclick={() => setFilter('feedback')}>💬 피드백 받은 학생</button>
  </div>

  {#each Object.entries(grouped) as [mentor, teams]}
    {@const total = Object.values(teams).flat().length}
    <section class="mentor-section">
      <div class="mentor-header">
        <h2>{mentor}</h2>
        <span class="count">{total}명</span>
      </div>

      {#each Object.entries(teams).sort() as [teamName, students]}
        <div class="team-section">
          <div class="team-header">
            <h3>{teamName}</h3>
            <span class="badge">{students.length}명</span>
          </div>
          <div class="grid">
            {#each students as s}
              <div class={getCardClass(s)}>
                <div class="card-header">
                  <div class="name">{s.student_name}</div>
                  <div class="badges">
                    {#if ISSUE_NO_TEXT.includes(s.student_name)}<span class="tag red">필수 누락</span>
                    {:else if ISSUE_FORMAT.includes(s.student_name)}<span class="tag amber">형식 보완</span>
                    {:else if EXEMPLARY.includes(s.student_name)}<span class="tag green">모범</span>{/if}
                    <span class="tag blue">{s.team_short}</span>
                    {#if s.has_feedback}<span class="tag gold">💬 피드백</span>{/if}
                  </div>
                </div>

                <!-- 1번 미션 -->
                <div class="section">
                  <div class="section-title">📎 1번 미션 ({s.mission1_files.length}개)</div>
                  {#if s.mission1_files.length > 0}
                    <div class="files">
                      {#each s.mission1_files as f}
                        <div class="file {fileExt(f.filename)}">{f.filename}</div>
                      {/each}
                    </div>
                  {:else}
                    <div class="empty-state">파일 없음</div>
                  {/if}
                </div>

                <!-- 2번 미션 소감 -->
                <div class="section">
                  <div class="section-title">💬 2번 미션 - 소감</div>
                  {#if s.impression}
                    <div class="quote">{s.impression}</div>
                  {:else}
                    <div class="quote empty">미기재 (필수)</div>
                  {/if}
                </div>

                <!-- 다음 액션 -->
                <div class="section">
                  <div class="section-title">🎯 다음에 할 것</div>
                  {#if s.next_action}
                    <div class="quote">{s.next_action}</div>
                  {:else}
                    <div class="quote empty">미기재 (필수)</div>
                  {/if}
                </div>

                <!-- 2번 미션 첨부 -->
                {#if s.mission2_files.length > 0}
                  <div class="section">
                    <div class="section-title">📎 2번 미션 첨부 ({s.mission2_files.length}개)</div>
                    <div class="files">
                      {#each s.mission2_files as f}
                        <div class="file {fileExt(f)}">{f}</div>
                      {/each}
                    </div>
                  </div>
                {/if}

                <!-- 멘토 피드백 -->
                {#if s.has_feedback}
                  <div class="mentor-feedback-section">
                    <div class="mentor-feedback-title">💬 멘토 피드백</div>
                    <div class="mentor-summary">{s.mentor_feedback_summary}</div>
                    <div class="feedback-points">
                      {#each s.mentor_feedback_points as p}
                        <div class="feedback-point">
                          <span class="feedback-cat {getCatClass(p.category)}">{p.category}</span>
                          <span class="feedback-text">{p.text}</span>
                        </div>
                      {/each}
                    </div>
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        </div>
      {/each}
    </section>
  {/each}
</div>

<style>
  /*
   * Cafe-latte palette consumed via CSS custom properties.
   * Source of truth: app/frontend/css/themes/cafe-latte.css
   * No raw hex values should live in this block.
   */
  /*
   * Body background scoped via :has() — only applied when this page
   * (which sets .cafe-latte on its root container) is mounted.
   * Avoids leaking the cream bg into unrelated routes.
   */
  :global(body:has(.cafe-latte)) {
    background: #FAFAF8; /* literal: :global() rule cannot inherit the .cafe-latte cascade */
  }
  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 32px 24px;
    font-family: 'SUIT Variable', -apple-system, sans-serif;
    color: var(--cl-text-strong);
    line-height: 1.6;
  }
  header {
    text-align: center;
    padding: 20px 0 32px;
    border-bottom: 1px solid var(--cl-border-default);
    margin-bottom: 32px;
  }
  header h1 { font-size: 28px; font-weight: 800; margin-bottom: 8px; letter-spacing: -0.02em; }
  header .subtitle { font-size: 14px; color: var(--cl-text-muted); margin-bottom: 24px; }
  .stats { display: flex; justify-content: center; gap: 32px; flex-wrap: wrap; }
  .stat { text-align: center; }
  .stat .number { font-size: 26px; font-weight: 700; color: var(--cl-accent); }
  .stat .label { font-size: 12px; color: var(--cl-text-muted); margin-top: 4px; }

  .filter-bar { display: flex; gap: 8px; margin-bottom: 32px; flex-wrap: wrap; justify-content: center; }
  .filter-btn {
    padding: 8px 16px;
    border: 1px solid var(--cl-border-default);
    background: var(--cl-bg-card);
    border-radius: 999px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: var(--cl-text-body);
    transition: all 0.15s;
  }
  .filter-btn:hover { background: var(--cl-bg-page); }
  .filter-btn.active { background: var(--cl-text-strong); color: var(--cl-bg-card); border-color: var(--cl-text-strong); }

  .mentor-section { margin-bottom: 56px; }
  .mentor-header {
    display: flex; align-items: baseline; gap: 12px;
    margin-bottom: 24px; padding-bottom: 16px;
    border-bottom: 2px solid var(--cl-text-strong);
  }
  .mentor-header h2 { font-size: 22px; font-weight: 700; }
  .mentor-header .count { font-size: 14px; color: var(--cl-text-muted); }

  .team-section { margin-bottom: 32px; }
  .team-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
  .team-header h3 { font-size: 16px; font-weight: 600; color: var(--cl-text-body); }
  .team-header .badge {
    font-size: 11px; padding: 2px 8px; border-radius: 4px;
    background: var(--cl-accent-soft); color: var(--cl-accent); font-weight: 600;
  }

  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(380px, 1fr)); gap: 16px; }

  .card {
    background: var(--cl-bg-card); border: 1px solid var(--cl-border-default); border-radius: 12px;
    padding: 20px; transition: all 0.2s; display: flex; flex-direction: column;
  }
  .card:hover { border-color: var(--cl-border-hover); box-shadow: var(--cl-shadow-card); }
  .card.has-issue { border-color: var(--cl-warn-fg); background: linear-gradient(to bottom, var(--cl-warn-bg) 0%, var(--cl-bg-card) 60px); }
  .card.no-text { border-color: var(--cl-danger-fg); background: linear-gradient(to bottom, var(--cl-danger-bg) 0%, var(--cl-bg-card) 60px); }
  .card.exemplary { border-color: var(--cl-ok-fg); background: linear-gradient(to bottom, var(--cl-ok-bg) 0%, var(--cl-bg-card) 60px); }
  .card.has-feedback { box-shadow: 0 0 0 1px var(--cl-accent); }

  .card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
  .name { font-size: 18px; font-weight: 700; }
  .badges { display: flex; gap: 4px; flex-wrap: wrap; }
  .tag { font-size: 10px; padding: 2px 8px; border-radius: 4px; font-weight: 600; }
  .tag.green { background: var(--cl-ok-bg); color: var(--cl-ok-fg); }
  .tag.red   { background: var(--cl-danger-bg); color: var(--cl-danger-fg); }
  .tag.amber { background: var(--cl-warn-bg); color: var(--cl-warn-fg); }
  .tag.blue  { background: var(--cl-info-bg); color: var(--cl-info-fg); }
  .tag.gold  { background: var(--cl-accent-soft); color: var(--cl-accent); }

  .section { margin-top: 16px; padding-top: 16px; border-top: 1px dashed var(--cl-border-default); }
  .section:first-of-type { border-top: none; padding-top: 0; margin-top: 0; }
  .section-title {
    font-size: 11px; font-weight: 700; color: var(--cl-text-muted);
    text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px;
  }
  .files { display: flex; flex-direction: column; gap: 6px; }
  .file {
    font-size: 12px; padding: 6px 10px; background: var(--cl-bg-muted);
    border-radius: 6px; color: var(--cl-text-body); font-family: 'IBM Plex Mono', monospace;
    word-break: break-all; border-left: 3px solid var(--cl-info-fg);
  }
  .file.pdf  { border-left-color: var(--cl-danger-fg); }
  .file.xlsx { border-left-color: var(--cl-ok-fg); }
  .file.png  { border-left-color: var(--cl-accent-purple); }

  .quote {
    font-size: 13px; line-height: 1.7; color: var(--cl-text-body);
    padding: 10px 12px; background: var(--cl-bg-muted); border-radius: 6px;
    border-left: 3px solid var(--cl-text-faint); margin-top: 4px;
  }
  .quote.empty {
    color: var(--cl-danger-fg); font-style: italic;
    border-left-color: var(--cl-danger-fg); background: var(--cl-danger-bg);
  }
  .empty-state { color: var(--cl-text-faint); font-size: 12px; font-style: italic; }

  .mentor-feedback-section {
    margin-top: 16px;
    background: linear-gradient(135deg, var(--cl-accent-soft) 0%, var(--cl-accent-soft-2) 100%);
    border-left: 4px solid var(--cl-accent);
    margin-left: -20px; margin-right: -20px; margin-bottom: -20px;
    border-radius: 0 0 12px 12px;
    padding: 14px 20px 18px;
  }
  .mentor-feedback-title {
    font-size: 11px; font-weight: 700; color: var(--cl-accent);
    margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.08em;
  }
  .mentor-summary {
    font-size: 13px; font-weight: 600; color: var(--cl-text-strong);
    margin-bottom: 12px; padding: 8px 12px; background: var(--cl-bg-card);
    border-radius: 6px; line-height: 1.5;
  }
  .feedback-points { display: flex; flex-direction: column; gap: 6px; }
  .feedback-point {
    font-size: 12px; line-height: 1.5; padding: 7px 10px;
    background: var(--cl-bg-feedback); border-radius: 4px;
  }
  .feedback-cat { font-size: 10px; font-weight: 700; margin-right: 6px; }
  .feedback-cat.cat-good   { color: var(--cl-ok-fg); }
  .feedback-cat.cat-action { color: var(--cl-info-fg); }
  .feedback-cat.cat-warn   { color: var(--cl-warn-fg); }
  .feedback-cat.cat-long   { color: var(--cl-accent-purple); }
  .feedback-text { color: var(--cl-text-body); }

  @media (max-width: 768px) {
    .grid { grid-template-columns: 1fr; }
    .container { padding: 16px; }
  }
</style>
